// Archetype taxonomy as accepted by the /commandercardstats and /upgrades
// archetype filter (see ARCHETYPE_BINS_API_CHANGES.md). Distinct from the
// FilterPanel's `archetypeOptions.js` taxonomy which uses lowercase ids,
// includes "any" sentinels, and has a slightly different sub list.
//
// The lists below are emission-derived — they include the static
// taxonomy plus pairs the backend aggregator can emit via the
// `dominantSubArchetype → CONTROL` rewrite rule and the
// second-major-as-sub fallback. They MUST stay in sync with the API's
// validation rules; surfacing a pair the API rejects would 400 the user.
//
// Major archetype ids match the API enum verbatim (UPPERCASE).
//
// `ANY` is the in-UI sentinel for "no filter" — never sent over the wire.

export const ARCHETYPE_ANY = 'ANY';

export const STATS_ARCHETYPE_MAJORS = [
    {
        id: 'MIDRANGE',
        label: 'Midrange',
        subs: [
            'COMBAT',
            'EXTRACOMBAT',
            'ANTHEM',
            'TOKENS',
            'PLUSONEPLUSONECOUNTERS',
            'BATTLECRUISER',
            'REANIMATOR',
            'VOLTRON',
            'POISON',
            'STOMPY',
            'BLINK',
            'ENCHANTRESS',
            'LANDSMATTER',
            'GROUPSLUG',
            'KINDRED',
            'EXALTED',
            // emission-derived fallbacks / rewrites
            'CONTROL',
            'COMBO',
        ],
    },
    {
        id: 'CONTROL',
        label: 'Control',
        subs: [
            'PILLOWFORT',
            'COUNTERS',
            'SPOTREMOVAL',
            'BOARDWIPES',
            'MLD',
            'THEFT',
            'STAX',
            'TAXES',
            'GOAD',
            'GROUPHUG',
            'SUPERFRIENDS',
            'TURNS',
            // emission-derived fallbacks / rewrites
            'CONTROL',
            'MIDRANGE',
            'COMBO',
        ],
    },
    {
        id: 'COMBO',
        label: 'Combo',
        subs: [
            'ARISTOCRATS',
            'WHEELS',
            'MILL',
            'SPELLSLINGER',
            'STORM',
            'COMBO',
            // emission-derived fallbacks / rewrites
            'CONTROL',
            'MIDRANGE',
        ],
    },
];

export const STATS_ARCHETYPE_MAJOR_BY_ID = STATS_ARCHETYPE_MAJORS.reduce((acc, m) => {
    acc[m.id] = m;
    return acc;
}, {});

export function getStatsSubsForMajor(major) {
    return STATS_ARCHETYPE_MAJOR_BY_ID[major]?.subs || [];
}

export function isValidStatsArchetypePair(major, sub) {
    if (!major || major === ARCHETYPE_ANY) return !sub || sub === ARCHETYPE_ANY;
    const entry = STATS_ARCHETYPE_MAJOR_BY_ID[major];
    if (!entry) return false;
    if (!sub || sub === ARCHETYPE_ANY) return true;
    return entry.subs.includes(sub);
}

// Pretty-print a sub-archetype id (UPPERCASE enum) for UI display.
// A handful of compounds need explicit handling; the rest get title-cased.
const SUB_LABEL_OVERRIDES = {
    PLUSONEPLUSONECOUNTERS: '+1/+1 Counters',
    EXTRACOMBAT: 'Extra Combat',
    BOARDWIPES: 'Board Wipes',
    SPOTREMOVAL: 'Spot Removal',
    PILLOWFORT: 'Pillow Fort',
    GROUPHUG: 'Group Hug',
    GROUPSLUG: 'Group Slug',
    LANDSMATTER: 'Lands Matter',
    SUPERFRIENDS: 'Superfriends',
    MLD: 'MLD',
    BATTLECRUISER: 'Battlecruiser',
};

export function prettyStatsSub(id) {
    if (!id) return '';
    if (SUB_LABEL_OVERRIDES[id]) return SUB_LABEL_OVERRIDES[id];
    const lower = String(id).toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function prettyStatsMajor(id) {
    return STATS_ARCHETYPE_MAJOR_BY_ID[id]?.label || id || '';
}
